import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { logger, sanitizeError } from '@heidi/runtime/logger';
import type { NavigatePort } from '@heidi/runtime/shell';
import {
  FormFormat,
  TemplateCategory,
  TemplateVisibility,
} from '@heidi/schema/ml-scribe';
import { useSetAtom, useStore } from 'jotai';
import { useCallback, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { CreateTemplateRequest, ShareableTemplateFields } from '../../contracts';
import {
  createTemplate,
  invalidateAllTemplatesQuery,
  isXfaPdfFormTemplate,
  useMutateCreateLatestVersionControl,
  useMutateCreateTemplateCreationRecord,
  useMutateUpdateTemplateCreationRecord,
  useMutateUpdateVersionControl,
} from '../../data';
import { useIsJsonFormEnabled, useIsXmlFormEnabled } from '../../flags';
import {
  activeTabAtom,
  isFromTemplateShareImportAtom,
  selectedVersionControlIdAtomFamily,
  templateCreationRecordIdAtom,
} from '../template-builder/atoms';

interface UseImportSharedTemplateOptions {
  onNavigate: NavigatePort;
  /** Called after a successful import, before navigation */
  onSuccess?: () => void;
  /** Called after a failed import, after toast is shown */
  onError?: () => void;
}

const xmlImportMessages = defineMessages({
  unavailable: {
    defaultMessage: "This form template can't be imported yet.",
    id: 'ny+E3EuiBq',
    description:
      'Toast when importing a shared XML form template while the feature is disabled',
  },
  jsonUnavailable: {
    defaultMessage: "This form template can't be imported yet.",
    id: 'eYW+34hVsl',
    description:
      'Toast when importing a shared JSON form template while the feature is disabled',
  },
});

function buildFormTemplateImportRequest(
  templateData: ShareableTemplateFields
): CreateTemplateRequest | null {
  if (templateData.template_category !== TemplateCategory.FORM_TEMPLATE) {
    return null;
  }
  const shared = {
    name: templateData.name,
    template_category: TemplateCategory.FORM_TEMPLATE,
    template_visibility: TemplateVisibility.User,
    convert_to_json: false,
    problem_based_charting_enabled: false,
  };
  if (templateData.form_format === FormFormat.json && templateData.json_source) {
    return {
      ...shared,
      trigger: 'from_json' as const,
      json_source: templateData.json_source,
      form_fields: templateData.form_fields ?? [],
    };
  }
  if (templateData.form_format === FormFormat.xml && templateData.xml_source) {
    return {
      ...shared,
      trigger: 'from_xml' as const,
      xml_source: templateData.xml_source,
      form_fields: templateData.form_fields ?? [],
    };
  }
  if (templateData.s3_object_key) {
    return {
      ...shared,
      trigger: 'from_pdf' as const,
      s3_object_key: templateData.s3_object_key,
      form_fields: templateData.form_fields ?? undefined,
    };
  }
  return null;
}

export function useImportSharedTemplate(
  templateData: ShareableTemplateFields | null,
  options: UseImportSharedTemplateOptions
) {
  const { onNavigate, onSuccess, onError } = options;
  const { t } = useTranslations();
  const intl = useIntl();
  const isXmlFormEnabled = useIsXmlFormEnabled();
  const isJsonFormEnabled = useIsJsonFormEnabled();
  const store = useStore();

  const setTemplateCreationRecordId = useSetAtom(templateCreationRecordIdAtom);
  const setActiveTab = useSetAtom(activeTabAtom);
  const setIsFromTemplateShareImport = useSetAtom(isFromTemplateShareImportAtom);

  const { mutateAsync: createTemplateCreationRecord } =
    useMutateCreateTemplateCreationRecord();
  const { mutateAsync: createLatestVersionControl } =
    useMutateCreateLatestVersionControl();
  const { mutateAsync: updateTemplateCreationRecord } =
    useMutateUpdateTemplateCreationRecord();
  const { mutateAsync: updateVersionControl } = useMutateUpdateVersionControl();

  const [isImporting, setIsImporting] = useState(false);
  const [loadingText, setLoadingText] = useState(t('templates.import.loading'));
  const hasStartedImport = useRef(false);

  const importTemplate = useCallback(async () => {
    if (!templateData || hasStartedImport.current) return;
    hasStartedImport.current = true;
    setIsImporting(true);

    try {
      // A shared link can carry an XML or JSON form template regardless of
      // the importer's flags; without the feature the import must fail cleanly
      // rather than create a template the importer cannot use.
      if (
        templateData.template_category === TemplateCategory.FORM_TEMPLATE &&
        templateData.form_format === FormFormat.xml &&
        templateData.xml_source &&
        !isXmlFormEnabled
      ) {
        throw new Error(intl.formatMessage(xmlImportMessages.unavailable));
      }
      // Sharing is hidden for XFA PDF forms, but a link built elsewhere can
      // still carry one; importing it by its PDF key would create a form with
      // no fields.
      if (isXfaPdfFormTemplate(templateData)) {
        throw new Error(intl.formatMessage(xmlImportMessages.unavailable));
      }

      if (
        templateData.template_category === TemplateCategory.FORM_TEMPLATE &&
        templateData.form_format === FormFormat.json &&
        templateData.json_source &&
        !isJsonFormEnabled
      ) {
        throw new Error(intl.formatMessage(xmlImportMessages.jsonUnavailable));
      }

      // Form templates use the form creation paths (PDF, XML or JSON) instead of
      // the template creation record flow, since they have no structure_template.
      const formTemplateRequest = buildFormTemplateImportRequest(templateData);
      if (formTemplateRequest) {
        setLoadingText(t('templates.import.importing'));
        await createTemplate({
          template: formTemplateRequest,
          saveAnyway: true,
        });
        await invalidateAllTemplatesQuery();
        setIsFromTemplateShareImport(true);
        onSuccess?.();
        try {
          await onNavigate('/scribe/my-templates');
        } catch (navigationError) {
          logger.error('Navigation after form template import failed', {
            error: sanitizeError(navigationError),
          });
        } finally {
          setIsImporting(false);
        }
        return;
      }

      if (!templateData.structure_template) {
        throw new Error(t('templates.import.missingStructure'));
      }

      setLoadingText(t('templates.import.creating'));

      const { id: templateCreationRecordId } = await createTemplateCreationRecord({
        trigger: 'from_chat',
        request: `Create a template called "${templateData.name}"`,
      });

      setTemplateCreationRecordId(templateCreationRecordId);

      setLoadingText(t('templates.import.settingUp'));

      const newVersionControl = await createLatestVersionControl({
        data: {},
        templateCreationRecordId,
      });

      const versionControlId = newVersionControl.id;

      store.set(
        selectedVersionControlIdAtomFamily(templateCreationRecordId),
        versionControlId
      );

      await updateTemplateCreationRecord({
        templateCreationRecordId,
        data: {
          template_name: templateData.name,
          selected_version_control_id: versionControlId,
        },
      });

      setLoadingText(t('templates.import.importing'));

      // The shared structure is copied as-is; generating one from the name
      // would be overwritten here, and the model refuses abbreviated names.
      await updateVersionControl({
        templateCreationRecordId,
        versionControlId,
        data: {
          template_structure: templateData.structure_template,
        },
      });

      setActiveTab('structure');
      setIsFromTemplateShareImport(true);
      onSuccess?.();

      try {
        await onNavigate(
          `/scribe/my-templates/template-creation-record/${templateCreationRecordId}`
        );
      } catch (navigationError) {
        logger.error('Navigation after import failed', {
          error: sanitizeError(navigationError),
        });
      } finally {
        setIsImporting(false);
      }
    } catch (error) {
      hasStartedImport.current = false;
      setIsImporting(false);
      toast({
        tone: 'destructive',
        title: t('templates.import.importErrorTitle'),
        description:
          error instanceof Error ? error.message : t('templates.import.unknownError'),
      });
      onError?.();
    }
  }, [
    templateData,
    intl,
    isJsonFormEnabled,
    isXmlFormEnabled,
    createTemplateCreationRecord,
    createLatestVersionControl,
    onError,
    onNavigate,
    onSuccess,
    setActiveTab,
    setIsFromTemplateShareImport,
    setTemplateCreationRecordId,
    store,
    t,
    updateTemplateCreationRecord,
    updateVersionControl,
  ]);

  return {
    isImporting,
    loadingText,
    importTemplate,
  };
}
